import React, { useState, useEffect, Fragment } from "react";
import { UserAccess, UserHasAccess } from "components/user-access";
import { AlertUnsavedData, Modal, NotificationToaster } from "components/notifications";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import { useDirtyData } from 'hooks/useDirtyData';
import { Wizard } from "components/wizard";
import { FormDateInput, FormNumericInput, FormRadio, FormSelect, FormTextInput } from 'components/form-fields';
import axios from 'axios';
import { OperatingLicenceService } from "services";
import { Intent } from "@blueprintjs/core";
import { useValidation } from "hooks/useValidation";
import moment from 'moment';
import { ShowHide } from "components/layout";
import { GridLayout } from "components/grid";
import { Button } from "components/buttons";

export function OperatingLicenceModal(
    { 
        showModal, 
        modalTitle,
        showFinishOnAllSteps,
        requiredActions,
        onSave, 
        onCloseModal, 
        licenceDetails
    }
) {
    const canAccessPage = UserHasAccess(requiredActions);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const operatingLicenceModel = {
        licenceArea: null,
        licenceType: null,
        licenceNumber: '',
        licenceRenewal: null,
        earnedRecognitionOperator: null,
        ocrsRoadScore: null,
        ocrsTrafficScore: null,
        ocrsCombinedScore: null,
        earnedRecognitionNumber: '',
        vehicleInspectionFrequency: null,
        trailerInspectionFrequency: null
    };
    
    const [originalOperatingLicence, setOriginalOperatingLicence] = useState(operatingLicenceModel);
    const [operatingLicence, setOperatingLicence] = useState(originalOperatingLicence);
    const isOperatingLicenceDirty = useDirtyData(originalOperatingLicence, operatingLicence);
    const [isOperatingLicenceValid, operatingLicenceErrors, validateOperatingLicence] = useValidation();

    const [wizardStep, setWizardStep] = useState(0);
    const [wizardValid, setWizardValid] = useState(false);

    const [licenceAreas, setLicenceAreas] = useState([]);
    const [licenceTypes, setLicenceTypes] = useState([]);

    const [blockModalClose, setBlockModalClose] = useState(false);

    useEffect(initialLoad, []);

    function initialLoad() { 
        if (!canAccessPage) {
            return;
        }

        axios.all([
            OperatingLicenceService.getOperatorLicenceOptions()
        ]).then(axios.spread(function (operatorLicenceOptionsResponse) {
            setLicenceAreas(operatorLicenceOptionsResponse.licenceAreas);
            setLicenceTypes(operatorLicenceOptionsResponse.licenceTypes);
            setLoading(false);
        }), function () {
            NotificationToaster.show(Intent.DANGER, "Unable to load, please try again.");
            setLoading(false);
        });
    }

    useEffect(loadDetails, [licenceDetails]);

    function loadDetails() {
        if (!licenceDetails) {
            return;
        }

        const mappedLicenceDetails = {
            licenceArea: licenceDetails.licenceArea,
            licenceType: licenceDetails.licenceType,
            licenceNumber: licenceDetails.licenceNumber,
            licenceRenewal: licenceDetails.licenceRenewal,
            earnedRecognitionOperator: licenceDetails.earnedRecognitionOperator,
            ocrsRoadScore: licenceDetails.ocrs?.roadScore,
            ocrsTrafficScore: licenceDetails.ocrs?.trafficScore,
            ocrsCombinedScore: licenceDetails.ocrs?.combinedScore,
            earnedRecognitionNumber: licenceDetails.ocrs?.earnedRecognitionNumber,
            vehicleInspectionFrequency: licenceDetails.vehicleInspectionFrequency,
            trailerInspectionFrequency: licenceDetails.trailerInspectionFrequency
        }

        setOperatingLicence(mappedLicenceDetails);
        setOriginalOperatingLicence(mappedLicenceDetails); 
    }

    function onValueChange(newValue, field, controlType = "default") {
        let passedValue = newValue;
        
        if (controlType === "date") { 
            passedValue = newValue ? moment(newValue).format("YYYY-MM-DD") : null
        }

        if (controlType === "radio") {
            passedValue = newValue === "true"; 
        }

        passedValue = passedValue === "" ? null : passedValue;

        setOperatingLicence((prevValue) => {
            let clonedScores = { ...prevValue }
            clonedScores[field] = passedValue;
    
            return clonedScores;
        })
    }

    useEffect(validate, [operatingLicence, isOperatingLicenceDirty, isOperatingLicenceValid])

    function validate() { 
        if (isOperatingLicenceDirty) {
            let rules = [
                { fieldName: 'licenceArea', required: true },
                { fieldName: 'licenceType', required: true },
                { fieldName: 'licenceNumber', required: true, minLength: 3 }
            ];

            validateOperatingLicence(rules, operatingLicence)
        }
    }

    function onSaveClick() {
        if (!isOperatingLicenceValid || !isOperatingLicenceDirty) {
            return;
        }

        setSaving(true);
    
        onSave(operatingLicence).then(
            () => {
                setSaving(false);
            },
            () => {
                setSaving(false);
            }
        );
    }

    function onWizardChange(step) {
        setWizardStep(step);
    }

    useEffect(setIfWizardValid, [wizardStep, isOperatingLicenceValid, isOperatingLicenceDirty]);

    function setIfWizardValid() {
        var valid = isOperatingLicenceValid;

        setWizardValid(valid);
    }

    function showOcrsScoreFields() {
        return operatingLicence.earnedRecognitionOperator !== null 
            && operatingLicence.earnedRecognitionOperator !== undefined
            && !operatingLicence.earnedRecognitionOperator;
    }

    function showEarnedRecognitionNumber() {
        return operatingLicence.earnedRecognitionOperator !== null 
            && operatingLicence.earnedRecognitionOperator !== undefined
            && operatingLicence.earnedRecognitionOperator;
    }

    function getAdditionalButtons() {
        return (
            <Button 
                intent="secondary" 
                loading={loading} 
                text="Close" 
                onClick={onCloseClick} 
                disabled={saving || loading} 
                id="close-btn"
            />
        );
    }

    function onCloseClick(forceClose = false) {
        if (isOperatingLicenceDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }

        setOperatingLicence(operatingLicenceModel);
        setOriginalOperatingLicence(operatingLicenceModel);
        onCloseModal();
    }

    return (
        <Fragment>
            <Modal 
                isOpen={showModal}
                onClose={onCloseClick}
                clickOutsideClose={true}
                title={modalTitle}
                isCloseButtonShown={false}
                updateStateOnClose={false}
                className='operating-licence-modal'
            >
                <UserAccess 
                    perform={requiredActions}
                    yes={() => {
                        return (
                            <>
                                <Wizard 
                                    loading={loading} 
                                    onStepChange={onWizardChange} 
                                    onFinish={onSaveClick}
                                    canProceed={wizardValid} 
                                    showFinishOnAllSteps={showFinishOnAllSteps}
                                    disabled={saving}
                                    alignButtonsRight={true}
                                    additionalButtonsStart={getAdditionalButtons()}
                                    finishButtonText="Save and Close"
                                >
                                    <div>
                                        <h2 id="header-licence-details">Licence Details</h2>
                                        
                                        <GridLayout numberOfColumns={2} className="spacer-bottom">
                                            
                                            <FormSelect
                                                disabled={saving || loading}
                                                loading={loading}
                                                items={licenceAreas}
                                                onItemSelect={(item) => onValueChange(item.id, "licenceArea")}
                                                placeholder="Select licence area"
                                                headingText="Select the licence area to add:"
                                                selectedValue={operatingLicence.licenceArea}
                                                dangerHelperText={operatingLicenceErrors.licenceArea}
                                                id="input-licence-area"
                                            ></FormSelect>
                                            
                                            <FormSelect
                                                disabled={saving || loading}
                                                loading={loading}
                                                items={licenceTypes}
                                                onItemSelect={(item) => onValueChange(item.id, "licenceType")}
                                                placeholder="Select licence type"
                                                headingText="Select the licence type to add:"
                                                selectedValue={operatingLicence.licenceType}
                                                dangerHelperText={operatingLicenceErrors.licenceType}
                                                id="input-licence-type"
                                            ></FormSelect>

                                            <FormTextInput
                                                id="input-licence-number"
                                                disabled={saving || loading}
                                                loading={loading}
                                                value={operatingLicence.licenceNumber}
                                                headingText="Licence Number:"
                                                onChange={(event) => onValueChange(event.target.value, "licenceNumber")}
                                                dangerHelperText={operatingLicenceErrors.licenceNumber}
                                            ></FormTextInput>

                                            <FormDateInput
                                                disabled={saving || loading}
                                                loading={loading}
                                                headingText="Licence Renewal Date:"
                                                onChange={(event) => onValueChange(event, "licenceRenewal", "date")}
                                                value={operatingLicence.licenceRenewal}
                                                dangerHelperText={operatingLicenceErrors.licenceRenewal}
                                                id="input-licence-renewal"
                                                minDate={moment().toDate()}
                                            ></FormDateInput>

                                        </GridLayout>
                                    </div>

                                    <div>
                                        <h2 id="header-earned-recognition-and-ocrs">Earned Recognition and OCRS</h2>
                                        <div className="inline-items spacer-bottom">
                                            <FormRadio
                                                disabled={saving || loading}
                                                loading={loading}
                                                onChange={(event) => onValueChange(event.target.value, "earnedRecognitionOperator", "radio")}
                                                headingText="Are you an earned recognition operator:"
                                                selectedValue={operatingLicence.earnedRecognitionOperator}
                                                dangerHelperText={operatingLicenceErrors.earnedRecognitionOperator}
                                                id="input-earned-recognition-operator"
                                            ></FormRadio>
                                        </div>
                                        <div>
                                            <ShowHide
                                                evaluator={showOcrsScoreFields()}
                                                show={(
                                                    <GridLayout numberOfColumns={2} className="spacer-bottom">
                                                        <FormNumericInput
                                                            disabled={saving || loading}
                                                            loading={loading}
                                                            min={0}
                                                            allowDecimal={true}
                                                            selectedValue={operatingLicence.ocrsRoadScore}
                                                            onValueChange={(event) => onValueChange(event, "ocrsRoadScore")}
                                                            headingText={"Road Worthiness Score:"}
                                                            id="input-road-score"
                                                        ></FormNumericInput>
                                                        <FormNumericInput
                                                            disabled={saving || loading}
                                                            min={0}
                                                            allowDecimal={true}
                                                            selectedValue={operatingLicence.ocrsTrafficScore}
                                                            onValueChange={(event) => onValueChange(event, "ocrsTrafficScore")}
                                                            headingText={"Traffic Score:"}
                                                            loading={loading}
                                                            id="input-traffic-score"
                                                        ></FormNumericInput>
                                                        <FormNumericInput
                                                            disabled={saving || loading}
                                                            loading={loading}
                                                            min={0}
                                                            allowDecimal={true}
                                                            selectedValue={operatingLicence.ocrsCombinedScore}
                                                            onValueChange={(event) => onValueChange(event, "ocrsCombinedScore")}
                                                            headingText={"Combined Score:"}
                                                            id="input-traffic-score"
                                                        ></FormNumericInput>
                                                    </GridLayout>
                                                )}
                                            >
                                            </ShowHide>
                                            <ShowHide
                                                evaluator={showEarnedRecognitionNumber()}
                                                show={(
                                                    <div className="spacer-bottom">
                                                        <FormTextInput
                                                            id="input-earned-recognition-number"
                                                            disabled={saving || loading}
                                                            loading={loading}
                                                            value={operatingLicence.earnedRecognitionNumber}
                                                            headingText="Earned Recognition Number:"
                                                            onChange={(event) => onValueChange(event.target.value, "earnedRecognitionNumber")}
                                                            dangerHelperText={operatingLicenceErrors.earnedRecognitionNumber}
                                                        ></FormTextInput>
                                                    </div>
                                                )}
                                            >
                                            </ShowHide>
                                        </div>
                                    </div>

                                    <div>
                                        <h1 id="header-inspection-details">Inspection Details</h1>
                                        <GridLayout numberOfColumns={2} className="spacer-bottom">
                                            <FormNumericInput
                                                disabled={saving || loading}
                                                min={1}
                                                allowDecimal={false}
                                                selectedValue={operatingLicence.vehicleInspectionFrequency}
                                                onValueChange={(event) => onValueChange(event, "vehicleInspectionFrequency")}
                                                headingText={"Vehicle Inspection Frequency:"}
                                                dangerHelperText={operatingLicenceErrors.vehicleInspectionFrequency}
                                                loading={loading}
                                                id="input-vehicle-inspection-frequency"
                                            ></FormNumericInput>
                                            <FormNumericInput
                                                disabled={saving || loading}
                                                min={1}
                                                allowDecimal={false}
                                                selectedValue={operatingLicence.trailerInspectionFrequency}
                                                onValueChange={(event) => onValueChange(event, "trailerInspectionFrequency")}
                                                headingText={"Vehicle Inspection Frequency:"}
                                                dangerHelperText={operatingLicenceErrors.trailerInspectionFrequency}
                                                loading={loading}
                                                id="input-trailer-inspection-frequency"
                                            ></FormNumericInput>
                                        </GridLayout>
                                    </div>

                                </Wizard>
                            </>
                        )
                    }}
                    no={() => <PageRestricted/>}
                />

            </Modal>
            <AlertUnsavedData
                isDirty={isOperatingLicenceDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => onCloseClick(true)}
            />
        </Fragment>
    );
}

OperatingLicenceModal.defaultProps = {
    showFinishOnAllSteps: false,
    isUpdate: false
};

OperatingLicenceModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string.isRequired,
    showFinishOnAllSteps: PropTypes.bool,
    requiredActions: PropTypes.array.isRequired,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    licenceDetails: PropTypes.object.isRequired
};